import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { clientErrorMessage } from "@/utils/apiErrors";
import { computed, ref, type ComputedRef, type InjectionKey } from "vue";

/** Reads the user-worded message of a refused request, or null for a failure the user cannot act on. */
export type RefusalMessage = (
  error: unknown
) => string | null | Promise<string | null>;

export interface BusyAction {
  busy: ComputedRef<boolean>;
  run: (
    action: () => Promise<void>,
    refusalMessage?: RefusalMessage
  ) => Promise<void>;
}

/**
 * The source card's write queue, shared with its devices section so that their saves to one
 * statement never overlap.
 */
export const sourceWritesKey: InjectionKey<BusyAction> = Symbol("sourceWrites");

/**
 * Runs a component's requests one after another, `busy` while any is queued or running.
 *
 * A refused request whose message `refusalMessage` (`clientErrorMessage` unless the run names
 * another) reads goes to the global warning handler; any other failure goes to the global error
 * handler.
 */
export function useBusyAction(): BusyAction {
  const { globalErrorHandler, globalWarningHandler } = useGlobalHandlers();
  const pending = ref(0);
  let queue: Promise<void> = Promise.resolve();

  function run(
    action: () => Promise<void>,
    refusalMessage: RefusalMessage = clientErrorMessage
  ): Promise<void> {
    pending.value += 1;
    queue = queue
      .then(() => attempt(action, refusalMessage))
      .finally(() => {
        pending.value -= 1;
      });
    return queue;
  }

  async function attempt(
    action: () => Promise<void>,
    refusalMessage: RefusalMessage
  ) {
    try {
      await action();
    } catch (error) {
      const message = await refusalMessage(error);
      if (message === null) globalErrorHandler(error);
      else globalWarningHandler(message, "");
    }
  }

  return { busy: computed(() => pending.value > 0), run };
}
